import { useEffect, useState } from "react";
import { useToast } from "@/components/toast";
import { useLoader } from "@/context/loaderContext";
import { useParams } from "react-router-dom";
import api from "@/components/customAxios/Axios";
import { Link } from "react-router-dom";

interface JobDetails {
    jd: string;
    role: string;
    location: string;
    salary_range: string;
    work_location_type: string;
    user_input_primary_skills: string[];
    user_input_secondary_skills: string[];
    years_of_experience: string;
    notice_period: string;
}

const JobDetailsPage = () => {
    const toast = useToast();
    const { setLoading } = useLoader();
    const [jobDetails, setJobDetails] = useState<JobDetails | null>(null);
    const params = useParams();

    const getJobDetails = async () => {
        setLoading(true);
        try {
            const response = await api.get(`/jobs/${params.id}`);
            setJobDetails(response.data.job_details);
        } catch (error: any) {
            toast.error({
                type: "background",
                duration: 5000,
                status: "error",
                title: "Error Fetching Job Details",
                description: error.response?.data?.error || error.response?.data?.message || "Failed to fetch job details. Please try again.",
                open: true,
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getJobDetails();
    }, []);

    const renderMultilineText = (text: string) =>
        text.split("\n").map((line, idx) => (
            <p key={idx} className="mb-2 text-gray-700 leading-relaxed">
                {line}
            </p>
        ));

    if (!jobDetails) return null;

    return (
        <div className="bg-white md:p-4  rounded-xl w-full min-h-[85vh] space-y-5">
            {/* Header */}
            <div>
                <div className="flex items-center gap-x-1 mb-4 p-0">
                    <button onClick={() => window.history.back()}>
                        <svg
                            width="20"
                            height="18"
                            viewBox="0 0 14 14"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M10.0154 0.733154L4.23065 6.51518C3.93728 6.79466 3.93728 7.23137 4.23065 7.51085L10.0155 13.2928"
                                stroke="#7F7F7F"
                                strokeMiterlimit="2.613"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </button>
                    <h2 className="text-3xl font-bold   ">Job Details</h2>
                </div>
                <Link to={`/respective-dashboard/${params.id}`} className="text-2xl font-bold mt-2 capitalize hover:underline text-[#438437] cursor-pointer">{jobDetails.role}</Link>

            </div>

            {/* Info Row */}
            <div className="flex flex-wrap items-center gap-4 text-gray-600">
                <div className="flex items-center gap-2">
                    <svg width="20" height="24" viewBox="0 0 20 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M17.4961 4.18046C16.4273 2.88929 15.043 1.89604 13.4774 1.29724C11.9119 0.698432 10.2182 0.514323 8.5605 0.762647C6.90283 1.01097 5.33743 1.68328 4.01606 2.71455C2.6947 3.74583 1.66219 5.10101 1.01862 6.64871C0.283576 8.42345 0.0914837 10.3765 0.466653 12.2604C0.841823 14.1444 1.76739 15.8747 3.12625 17.2324C3.27912 17.3849 3.48642 17.4705 3.70255 17.4705C3.91868 17.4704 4.12593 17.3846 4.27871 17.232C4.43149 17.0795 4.51729 16.8727 4.51723 16.657C4.51716 16.4413 4.43125 16.2344 4.27837 16.0819C3.14593 14.9524 2.37454 13.5119 2.06208 11.9432C1.74962 10.3746 1.91018 8.74844 2.52337 7.27118C3.13645 5.79312 4.17394 4.53005 5.50473 3.64151C6.83551 2.75297 8.39985 2.27882 10 2.27914C11.6004 2.2785 13.1651 2.75235 14.4962 3.64091C15.8273 4.52947 16.8651 5.79287 17.4782 7.27118C18.0914 8.74791 18.2519 10.3735 17.9394 11.9417C17.627 13.5098 16.8556 14.9496 15.7232 16.0785C15.6474 16.1542 15.5873 16.244 15.5463 16.3428C15.5052 16.4416 15.4841 16.5474 15.4841 16.6544C15.484 16.7614 15.5051 16.8674 15.5461 16.9662C15.5871 17.0651 15.6472 17.1548 15.7229 17.2304C15.7987 17.3061 15.8886 17.366 15.9876 17.407C16.0866 17.448 16.1927 17.4691 16.2999 17.4691C16.407 17.4691 16.5132 17.4481 16.6122 17.4072C16.7112 17.3663 16.8012 17.3065 16.877 17.2308C18.5857 15.5229 19.5959 13.239 19.7101 10.8259C19.8243 8.41268 19.0342 6.04368 17.4945 4.18205L17.4961 4.18046Z" fill="#848484" />
                        <path d="M3.69013 15.8311C3.52746 15.8304 3.36834 15.8786 3.23335 15.9693C3.09837 16.0601 2.99373 16.1894 2.93298 16.3403C2.87224 16.4912 2.85819 16.6568 2.89265 16.8157C2.9271 16.9747 3.00848 17.1197 3.12625 17.2319L8.72763 22.8234C9.06688 23.1574 9.52389 23.3447 10 23.3447C10.4761 23.3447 10.9331 23.1574 11.2724 22.8234L16.8738 17.2319C17.0265 17.0791 17.1124 16.8719 17.1124 16.6559C17.1124 16.4398 17.0265 16.2326 16.8738 16.0798C16.721 15.927 16.5138 15.8412 16.2977 15.8412C16.0816 15.8412 15.8744 15.927 15.7216 16.0798L10.1235 21.6729C10.0455 21.7493 9.9545 21.7493 9.8765 21.6729L4.27838 16.0798C4.20206 16.0011 4.11061 15.9386 4.00951 15.8961C3.90841 15.8537 3.79977 15.8304 3.69013 15.8311Z" fill="#848484" />
                        <path d="M10.0293 5.52734C7.34638 5.52734 5.15425 7.71947 5.15425 10.4023C5.15425 13.0836 7.34638 15.2773 10.0293 15.2773C12.7105 15.2773 14.9043 13.0836 14.9043 10.4023C14.9043 7.71947 12.7105 5.52734 10.0293 5.52734ZM10.0293 7.15552C10.4561 7.1538 10.8791 7.23655 11.2738 7.39911C11.6684 7.56166 12.0271 7.80088 12.3289 8.10271C12.6307 8.40454 12.8698 8.76312 13.0324 9.15781C13.1949 9.5525 13.2777 9.97549 13.276 10.4023C13.276 12.2061 11.8314 13.6523 10.0293 13.6523C8.2255 13.6523 6.7825 12.2061 6.7825 10.4023C6.78078 9.97549 6.86359 9.5525 7.02614 9.15781C7.1887 8.76312 7.42779 8.40454 7.72962 8.10271C8.03145 7.80088 8.39005 7.56166 8.78474 7.39911C9.17943 7.23655 9.6024 7.1538 10.0293 7.15552Z" fill="#848484" />
                    </svg>

                    <span className="capitalize">{jobDetails.location || "NA"}</span>
                </div>
                <div className="flex items-center gap-2">
                    <svg width="26" height="24" viewBox="0 0 26 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path fillRule="evenodd" clipRule="evenodd" d="M0.75 5.00033C0.75 4.84562 0.811458 4.69724 0.920854 4.58785C1.03025 4.47845 1.17862 4.41699 1.33333 4.41699H24.6667C24.8214 4.41699 24.9697 4.47845 25.0791 4.58785C25.1885 4.69724 25.25 4.84562 25.25 5.00033V13.167C25.25 13.3091 25.198 13.4464 25.1039 13.5529C25.0098 13.6595 24.8801 13.728 24.739 13.7457L15.4057 14.9123L15.261 13.755L24.0833 12.6525V5.58366H1.91667V12.6525L10.739 13.755L10.5943 14.9123L1.261 13.7457C1.11994 13.728 0.990177 13.6595 0.896082 13.5529C0.801987 13.4464 0.75004 13.3091 0.75 13.167V5.00033Z" fill="#939393" />
                        <path fillRule="evenodd" clipRule="evenodd" d="M10.0833 11.417C10.0833 11.2623 10.1448 11.1139 10.2542 11.0045C10.3636 10.8951 10.512 10.8337 10.6667 10.8337H15.3333C15.488 10.8337 15.6364 10.8951 15.7458 11.0045C15.8552 11.1139 15.9167 11.2623 15.9167 11.417V17.2503C15.9167 17.405 15.8552 17.5534 15.7458 17.6628C15.6364 17.7722 15.488 17.8337 15.3333 17.8337H10.6667C10.512 17.8337 10.3636 17.7722 10.2542 17.6628C10.1448 17.5534 10.0833 17.405 10.0833 17.2503V11.417ZM11.25 12.0003V16.667H14.75V12.0003H11.25ZM8.91667 3.25033C8.91667 2.63149 9.1625 2.03799 9.60009 1.60041C10.0377 1.16282 10.6312 0.916992 11.25 0.916992H14.75C15.3688 0.916992 15.9623 1.16282 16.3999 1.60041C16.8375 2.03799 17.0833 2.63149 17.0833 3.25033V5.00033H15.9167V3.25033C15.9167 2.94091 15.7938 2.64416 15.575 2.42537C15.3562 2.20658 15.0594 2.08366 14.75 2.08366H11.25C10.9406 2.08366 10.6438 2.20658 10.425 2.42537C10.2063 2.64416 10.0833 2.94091 10.0833 3.25033V5.00033H8.91667V3.25033Z" fill="#939393" />
                        <path fillRule="evenodd" clipRule="evenodd" d="M1.91667 22.5V13.75H3.08334V21.9167H22.9167V13.75H24.0833V22.5C24.0833 22.6547 24.0219 22.8031 23.9125 22.9125C23.8031 23.0219 23.6547 23.0833 23.5 23.0833H2.50001C2.3453 23.0833 2.19692 23.0219 2.08753 22.9125C1.97813 22.8031 1.91667 22.6547 1.91667 22.5Z" fill="#939393" />
                    </svg>


                    <span>{jobDetails.work_location_type || "NA"}</span>
                </div>
                <div className="flex items-center gap-2">
                    <svg width="21" height="23" viewBox="0 0 21 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M14.875 0.541016V4.916M6.125 0.541016V4.916M0.65625 9.291H20.3438M18.1562 2.7285H2.84375C1.63563 2.7285 0.65625 3.70789 0.65625 4.916V20.2285C0.65625 21.4366 1.63563 22.416 2.84375 22.416H18.1562C19.3644 22.416 20.3438 21.4366 20.3438 20.2285V4.916C20.3438 3.70789 19.3644 2.7285 18.1562 2.7285Z" stroke="#848484" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M10.5 12.8789V19.1451M11.9241 14.0182H9.7879C9.52355 14.0182 9.26994 14.1233 9.08298 14.3102C8.89602 14.4972 8.79102 14.7507 8.79102 15.0151C8.79102 15.2796 8.89602 15.5331 9.08298 15.7201C9.26994 15.907 9.52355 16.012 9.7879 16.012H11.2121C11.4764 16.012 11.7301 16.1171 11.917 16.3041C12.104 16.4909 12.209 16.7446 12.209 17.0089C12.209 17.2733 12.104 17.5269 11.917 17.7138C11.7301 17.9008 11.4764 18.0059 11.2121 18.0059H8.79102" stroke="#848484" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>




                    <span>{jobDetails.salary_range?.length > 4 ? jobDetails.salary_range : "NA"}</span>
                </div>
                {/* <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-green-600" />
                    <span className="text-green-600 bg-green-100 rounded-full px-3 py-1 text-sm">Posted 3 days ago</span>
                </div> */}
                {/* <div className="flex items-center gap-2 ml-auto">
                    <Users className="w-5 h-5" />
                    <span>82 Applicants</span>
                </div> */}
            </div>

            {/* Key Responsibilities */}
            <section>
                <h3 className="text-xl font-semibold mb-2">Key Responsibilities</h3>
                {renderMultilineText(jobDetails.jd)}
            </section>

            {/* Skills Required */}
            <section>
                {/* Skill(s) Required */}
                <section>
                    <h3 className="text-xl font-semibold mb-2">Skill(s) Required</h3>

                    {/* Must Have Skills */}
                    <div className="space-y-3 mb-4">
                        <h4 className="text-md font-medium  mb-1">Must Have Skills</h4>
                        <div className="flex flex-wrap gap-3">
                            {jobDetails.user_input_primary_skills?.length > 0 ? (
                                jobDetails.user_input_primary_skills.map((skill: string, idx: number) => (
                                    <span
                                        key={idx}
                                        className="bg-gray-100 text-gray-800 px-4 py-1 rounded-full text-sm capitalize"
                                    >
                                        {skill}
                                    </span>
                                ))
                            ) : (
                                <span className="text-gray-500">NA</span>
                            )}
                        </div>
                    </div>

                    {/* Good to Have Skills */}
                    <div>
                        <h4 className="text-md font-medium  mb-1">Good to Have Skills</h4>
                        <div className="flex flex-wrap gap-3">
                            {jobDetails.user_input_secondary_skills?.length > 0 ? (
                                jobDetails.user_input_secondary_skills.map((skill: string, idx: number) => (
                                    <span
                                        key={idx}
                                        className="bg-gray-100 text-gray-800 px-4 py-1 rounded-full text-sm capitalize"
                                    >
                                        {skill}
                                    </span>
                                ))
                            ) : (
                                <span className="text-gray-500">NA</span>
                            )}
                        </div>
                    </div>
                </section>

            </section>

            {/* Requirements */}
            <section>
                <h3 className="text-xl font-semibold mb-2">Requirements</h3>
                <ul className="text-gray-700 list-disc list-inside leading-relaxed space-y-1">
                    <li>Experience: {jobDetails.years_of_experience || "NA"}</li>
                    <li>Notice Period: {jobDetails.notice_period || "NA"}</li>
                    <li>Work Type: {jobDetails.work_location_type || "NA"}</li>
                    {/* <li>Primary Skill: {jobDetails.user_input_primary_skills?.join(", ") || "NA"}</li> */}
                </ul>
            </section>

        </div>
    );
};

export default JobDetailsPage;
